import { events } from "@constants/tracking";
import { useExamsFilterDialog } from "@contexts/DialogContext/dialogs/exams-filter";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import { useAppLogo } from "@features/exam/hooks/useAppLogo";
import { PreferencesMenu } from "@features/settings/components/PreferencesMenu";
import { useAppStore } from "@hooks/useAppStore";
import { isSafeUrl } from "@memorang/helpers/common";
import { isWeb } from "@memorang/helpers/platform";
import AppbarAction from "@memorang/ui/components/AppbarAction";
import Box from "@memorang/ui/components/Box";
import CustomAppBar from "@memorang/ui/components/CustomAppbar";
import type { CustomAppBarProps } from "@memorang/ui/components/CustomAppbar/types";
import { AppLogo } from "@memorang/ui/components/Navigation/AppLogo";
import { NAVIGATION_RAIL_HORIZONTAL_PADDING } from "@memorang/ui/components/Navigation/NavigationRail/constants";
import TooltipWrapper from "@memorang/ui/components/TooltipWrapper";
import { useAppConfig } from "@memorang/ui/contexts/AppConfigContext";
import { useDevice } from "@memorang/ui/useDevice";
import { trackCustomEvent } from "analytics";
import { useNavigation, useRouter } from "expo-router";
import { type AppBarVariant, resolveAppBarChrome } from "./helpers";

type Props = CustomAppBarProps & {
	hideExamSwitcher?: boolean;
	showLogo?: boolean;
	variant?: AppBarVariant;
};

export const AppBar = ({
	centerTitle,
	hideCommandBar,
	hideExamSwitcher,
	hideMenu,
	renderLeft,
	renderRight,
	showLogo = false,
	variant = "authenticated",
	...props
}: Props) => {
	const {
		currentExamContext: { examName },
	} = useExamContext();
	const { hasMultipleExams } = useMultipleExams();
	const { showExamsFilter } = useExamsFilterDialog();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();
	const router = useRouter();
	const { allowChangeExam } = useAppConfig();
	const { isMobile } = useDevice();
	const appLogo = useAppLogo();
	const companyUrl = useAppStore((store) => store.app.links.companyUrl);
	const chrome = resolveAppBarChrome({
		variant,
		hideCommandBar,
		hideExamSwitcher,
		hideMenu,
	});

	const tooltipTitle = companyUrl
		?.replace("https://", "")
		.replace("www.", "")
		.replace("/", "");

	const navigateToPartnerSite = () => {
		if (!isSafeUrl(companyUrl, { allowedSchemes: ["https"] })) {
			return;
		}
		window.open(companyUrl, "_blank", "noopener,noreferrer");
	};

	const renderPublicActions = () => (
		<Box flexDirection="row" alignItems="center">
			{!isMobile && isWeb ? (
				<TooltipWrapper title={`Go to ${tooltipTitle}`}>
					<AppbarAction
						icon="open-in-new"
						accessibilityRole="button"
						accessibilityLabel={`Go to ${tooltipTitle}`}
						onPress={navigateToPartnerSite}
					/>
				</TooltipWrapper>
			) : null}
			<PreferencesMenu />
		</Box>
	);

	return (
		<CustomAppBar
			navigation={navigation}
			centerTitle={variant === "public" || centerTitle}
			hideMenu={chrome.hideMenu}
			hideCommandBar={chrome.hideCommandBar}
			handleTrackEvent={() => {
				trackCustomEvent({
					eventName: events.commandBarClicked,
					option: "command_bar",
				});
			}}
			examSwitcher={
				allowChangeExam && hasMultipleExams && !chrome.hideExamSwitcher
					? {
							label: examName,
							onPress: showExamsFilter,
						}
					: undefined
			}
			renderLeft={
				variant === "public" && showLogo
					? () => (
							<Box paddingLeft={NAVIGATION_RAIL_HORIZONTAL_PADDING}>
								<AppLogo
									logo={appLogo}
									containerStyle={{ width: 100, height: 38 }}
									onPress={() => router.back()}
									accessibilityLabel="Company Logo"
								/>
							</Box>
						)
					: renderLeft
			}
			renderRight={variant === "public" ? renderPublicActions : renderRight}
			{...props}
		/>
	);
};
